import React from "react"
import ReactApexChart from "react-apexcharts"

import KpiCard from "../parts/KpiCard"
import MetricHint from "../parts/MetricHint"
import {
	Card, EmptyNote, baseChartOptions, CHART_COLORS,
	convert, formatAmount, formatNumber, formatPercent, mutedStyle,
} from "../parts/common"

/**
 * Удержание.
 *
 * Отток считается по активности, а не по статусу: status = -1 ставит автоматически
 * cronCheckPosCredit при отрицательном балансе, и почти все возвращаются, оплатив.
 * Считать это оттоком — значит показывать 100 % оттока в месяц.
 */
function Retention({ data, currency, rates }) {
	const months = data.months || []
	const cohorts = data.cohorts || []
	const statusChanges = data.status_changes || []
	const summary = data.summary || {}

	const amount = (value) => formatAmount(convert(value, null, currency, rates), currency)

	const churnChart = {
		options: {
			...baseChartOptions,
			chart: { ...baseChartOptions.chart, type: "line", height: 300 },
			colors: [CHART_COLORS[4], CHART_COLORS[0]],
			stroke: { width: [0, 3], curve: "smooth" },
			plotOptions: { bar: { borderRadius: 3, columnWidth: "50%" } },
			xaxis: { ...baseChartOptions.xaxis, categories: months.map((row) => row.label) },
			yaxis: [
				{ labels: { style: { colors: "#8791a1", fontSize: "11px" } } },
				{
					opposite: true,
					labels: {
						style: { colors: "#8791a1", fontSize: "11px" },
						formatter: (value) => Number(value).toFixed(1) + " %",
					},
				},
			],
		},
		series: [
			{ name: "Ушло брендов", type: "column", data: months.map((row) => row.churned_brands) },
			{ name: "Отток, %", type: "line", data: months.map((row) => row.churn_rate) },
		],
	}

	// Ширина матрицы когорт — по самой длинной когорте, но не больше 12 месяцев:
	// дальше таблица перестаёт помещаться на экран и её всё равно не читают.
	const maxIndex = Math.min(
		12,
		cohorts.reduce((max, cohort) => {
			const indexes = Object.keys(cohort.percents || {}).map(Number)
			return Math.max(max, indexes.length > 0 ? Math.max(...indexes) : 0)
		}, 0)
	)

	function cellColor(percent) {
		if (percent === undefined || percent === null) return "transparent"
		const alpha = Math.max(0.06, Math.min(1, percent / 100))
		return `rgba(76, 141, 240, ${alpha * 0.75})`
	}

	return (
		<>
			<div className="row g-2 mb-3">
				<div className="col-md-3">
					<KpiCard title="Отток в месяц" hintKey="metric_churn_hint"
						value={formatPercent(summary.avg_churn_rate, 2)}
						sublabel="среднее за 6 месяцев" accent="#d23434" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Средний срок жизни" hintKey="metric_lifetime_hint"
						value={summary.avg_lifetime_months ? `${summary.avg_lifetime_months} мес.` : "—"} />
				</div>
				<div className="col-md-3">
					<KpiCard title="ARPA" hintKey="metric_arpa_hint" value={amount(summary.arpa)}
						sublabel="средний доход с точки в месяц" />
				</div>
				<div className="col-md-3">
					<KpiCard title="LTV" hintKey="metric_ltv_hint"
						value={summary.ltv ? amount(summary.ltv) : "—"}
						sublabel="доход с клиента за всю жизнь" accent="#1f9d57" />
				</div>
			</div>

			<div className="row g-2 mb-2">
				<div className="col-md-7">
					<Card title="Отток по месяцам" hint={<MetricHint hintKey="metric_churn_hint" />}>
						{months.length === 0 && <EmptyNote>Нет данных</EmptyNote>}
						{months.length > 0 && (
							<ReactApexChart options={churnChart.options} series={churnChart.series}
								type="line" height={300} />
						)}
						<div style={mutedStyle}>{summary.note}</div>
					</Card>
				</div>

				<div className="col-md-5">
					<Card title="Блокировки и возвраты" hint={<MetricHint hintKey="metric_reactivation_hint" />}>
						{statusChanges.length === 0 && (
							<EmptyNote>Журнал статусов ведётся с августа 2026 — данных пока мало</EmptyNote>
						)}
						{statusChanges.length > 0 && (
							<div className="table-responsive">
								<table className="table table-sm table-bordered mb-0" style={{ fontSize: "12px" }}>
									<thead>
										<tr>
											<th className="text-center">Месяц</th>
											<th className="text-end">Отключено</th>
											<th className="text-end">Вернулось</th>
											<th className="text-end">Удалено</th>
										</tr>
									</thead>
									<tbody>
										{statusChanges.map((row) => (
											<tr key={row.month}>
												<td className="text-center">{row.label}</td>
												<td className="text-end">{formatNumber(row.suspended)}</td>
												<td className="text-end" style={{ color: "var(--dark-fg-success, #1f9d57)" }}>{formatNumber(row.reactivated)}</td>
												<td className="text-end" style={{ color: "var(--dark-fg-danger, #d23434)" }}>{formatNumber(row.deleted)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</Card>
				</div>
			</div>

			<Card title="Когортное удержание брендов" hint={<MetricHint hintKey="metric_cohort_hint" />}
				right={<span style={mutedStyle}>когорта — месяц первого чека</span>}>
				{cohorts.length === 0 && <EmptyNote>Недостаточно истории для когорт</EmptyNote>}
				{cohorts.length > 0 && (
					<div className="table-responsive">
						<table className="table table-sm table-bordered mb-0" style={{ fontSize: "11px" }}>
							<thead>
								<tr>
									<th className="text-center">Когорта</th>
									<th className="text-end">Брендов</th>
									{[...Array(maxIndex + 1)].map((item, index) => (
										<th key={index} className="text-center">{index === 0 ? "старт" : `+${index}`}</th>
									))}
								</tr>
							</thead>
							<tbody>
								{cohorts.map((cohort) => (
									<tr key={cohort.cohort}>
										<td className="text-center" style={{ whiteSpace: "nowrap" }}>{cohort.label}</td>
										<td className="text-end">{cohort.size}</td>
										{[...Array(maxIndex + 1)].map((item, index) => {
											const percent = cohort.percents?.[index]
											return (
												<td key={index} className="text-center"
													style={{ background: cellColor(percent), color: percent > 60 ? "#fff" : "var(--dark-text-strong, #111827)" }}>
													{percent === undefined ? "" : Math.round(percent) + "%"}
												</td>
											)
										})}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</Card>
		</>
	)
}

export default Retention
